import React, { ReactElement, useEffect } from "react";
import { Link, useNavigate, NavigateFunction } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import {
  RegisterContentProps,
  RegisterErrorContentProps,
  RegisterFormContentProps,
  RegisterMemberAlreadyVerifiedProps,
  RegisterMemberToVerifyContentProps,
  RegisterTextContentProps,
} from "./registerContent.model";
import { Button } from "@companion-core/shared/app/Components/Button";
import { VCESiframe } from "@companion-core/shared/app/Components/VCES/VCESiframe";
import { VCESvalidator } from "@companion-core/shared/app/Components/VCES/VCESvalidator";
import { DynamicForm } from "@companion-core/shared/app/Components/DynamicForm";
import { IndependentDynamicForm } from "@companion-core/shared/app/Components/DynamicForm/IndependentDynamicForm";
import {
  trackClickHelp,
  trackClickRegister,
  trackStepRegister,
} from "@companion-core/shared/app/tracking/register";
import { useRegisterContent } from "@companion-core/shared/app/Components/SignUp/hooks/useRegisterContent";
import "@companion-core/web/src/Assets/Styles/scss/register.scss";

const VCESContent = () => {
  useEffect(() => {
    trackStepRegister("vces_card_number");
  }, []);

  return <VCESiframe />;
};

const RegisterEntryContent = ({
  name,
  initialFormJSON,
  onSubmitForm,
}: RegisterFormContentProps) => {
  if (!initialFormJSON) {
    return null;
  }
  return (
    <IndependentDynamicForm
      name={name}
      initialFormJSON={initialFormJSON}
      onSubmitForm={onSubmitForm}
    />
  );
};

const RegisterNoEmailContent = ({
  name,
  onSubmitForm,
  initialFormJSON,
}: RegisterFormContentProps) => {
  if (!initialFormJSON) {
    return null;
  }
  return (
    <IndependentDynamicForm
      name={name}
      initialFormJSON={initialFormJSON}
      onSubmitForm={onSubmitForm}
    />
  );
};

const VCESDatafeedContent = ({ name, onSubmitForm, initialFormJSON }: RegisterFormContentProps) => {
  if (!initialFormJSON) {
    return null;
  }
  return (
    <IndependentDynamicForm
      name={name}
      initialFormJSON={initialFormJSON}
      scrollable
      onSubmitForm={onSubmitForm}
    />
  );
};

const VCESMemberContent = ({ name, onSubmitForm, initialFormJSON }: RegisterFormContentProps) => {
  if (!initialFormJSON) {
    return null;
  }
  return (
    <DynamicForm
      name={name}
      initialFormJSON={initialFormJSON}
      scrollable
      onSubmitForm={onSubmitForm}
    />
  );
};

const RegisterMemberToVerifyContent = ({
  t,
  onRetryToSendEmail,
  obfuscatedEmail,
}: RegisterMemberToVerifyContentProps) => {
  useEffect(() => {
    trackStepRegister("email_verification");
  }, []);

  return (
    <div className="register__member-to-verify">
      <div data-testid="register__member-to-verify--title" className="super-title-3">
        {t("register.member-to-verify.title")}
      </div>
      <div
        data-testid="register__member-to-verify--body"
        className="register__member-to-verify-body body-text-4">
        <div className="register__member-to-verify-body-text ">
          {obfuscatedEmail ? (
            <Trans
              i18nKey="register.member-to-verify.text-with-email"
              values={{ email: obfuscatedEmail }}
              components={{
                obf: (
                  <span
                    className="body-text-4"
                    data-testid="register__member-to-verify--obfuscated-email"
                  />
                ),
              }}
            />
          ) : (
            t("register.member-to-verify.text")
          )}
        </div>
        <div className="register__member-to-verify-body-question">
          {t("register.member-to-verify.tips")}
        </div>
        <span>{t("register.member-to-verify.need-help-link-prefix")}</span>
        <Link
          data-testid="register__link--retry"
          to="/register/member"
          className="body-text-3"
          onClick={onRetryToSendEmail}>
          {t("register.member-to-verify.link")}
        </Link>
      </div>
    </div>
  );
};

const RegisterMemberToVerifyConfirmationContent = ({ t }: RegisterTextContentProps) => {
  useEffect(() => {
    trackStepRegister("email_verification_confirmation");
  }, []);

  return (
    <div className="register__member-to-verify-confirmation">
      <div data-testid="register__member-to-verify-confirmation--title" className="super-title-3">
        {t("register.member-to-verify-confirmation.title")}
      </div>
      <div
        data-testid="register__member-to-verify-confirmation--body"
        className="register__member-to-verify-confirmation-body body-text-4">
        <div className="register__member-to-verify-confirmation-body-text body-text-4">
          {t("register.member-to-verify-confirmation.text")}
        </div>
        <div className="register__member-to-verify-confirmation-body-tips body-text-4">
          {t("register.member-to-verify-confirmation.tips")}
        </div>
        <div className="register__member-to-verify-confirmation-need-help-link-prefix body-text-4">
          {t("register.member-to-verify-confirmation.need-help-link-prefix")}
        </div>
        <Link
          data-testid="register__link--need-help"
          to="/need-help"
          className="body-text-3"
          onClick={() => {
            trackClickHelp();
          }}>
          {t("register.member-to-verify-confirmation.link")}
        </Link>
      </div>
    </div>
  );
};

const RegisterMemberNotFound = ({
  t,
  redirectCallback,
  error,
  setError,
}: RegisterErrorContentProps) => {
  useEffect(() => {
    error && trackStepRegister("err_info_perso", undefined, error);
    return () => {
      setError(null);
    };
  }, [error]);

  return (
    <div className="register__member-not-found">
      <div data-testid="register__member-not-found--title" className="super-title-3">
        {t("register.member-not-found.title")}
      </div>
      <div
        data-testid="register__member-not-found--body"
        className="register__member-not-found-body body-text-4">
        <div className="register__member-not-found-body-text">
          {t("register.member-not-found.text")}
        </div>
        <div className="register__member-not-found-body-tips">
          {t("register.member-not-found.tips")}
        </div>
      </div>
      <Button
        testID="register__member-not-found--button"
        classNames={["register__member-not-found-button"]}
        label={t("register.member-not-found.button")}
        onClick={() => {
          trackClickRegister("err_info_perso_retry");
          redirectCallback();
        }}
      />
    </div>
  );
};

const RegisterMemberAlreadyVerified = ({
  t,
  vcesEnabled,
  navigate,
  error,
  setError,
}: Omit<RegisterMemberAlreadyVerifiedProps, "redirectCallback"> & {
  navigate: NavigateFunction;
}) => {
  useEffect(() => {
    error && trackStepRegister("err_info_perso", undefined, error);
    return () => {
      setError(null);
    };
  }, [error]);

  return (
    <div data-testid="register__member-already-verified">
      <div data-testid="register__member-already-verified--title" className="super-title-3">
        {t(
          vcesEnabled
            ? "register.member-already-verified-vces.title"
            : "register.member-already-verified.title"
        )}
      </div>
      <div
        data-testid="register__member-already-verified--body"
        className="register__member-already-verified-body body-text-4">
        {vcesEnabled ? (
          <div>
            <div className="register__member-already-verified-body-text body-text-4">
              {t("register.member-already-verified-vces.text")}
            </div>
            <div className="body-text-4">{t("register.member-already-verified-vces.tips")}</div>
          </div>
        ) : (
          <div>
            <div className="register__member-already-verified-body-text body-text-4">
              {t("register.member-already-verified.text")}
            </div>
            <div className="body-text-4">{t("register.member-already-verified.tips")}</div>
          </div>
        )}
      </div>
      <Button
        testID="register__member-already-verified--button"
        classNames={["register__member-already-verified-button"]}
        label={t(
          vcesEnabled
            ? "register.member-already-verified-vces.button"
            : "register.member-already-verified.button"
        )}
        onClick={() => {
          if (vcesEnabled) {
            trackClickRegister("err_info_perso_need_help");
            navigate("/need-help");
          } else {
            trackClickRegister("err_info_perso_login");
            navigate("/login");
          }
        }}
      />
    </div>
  );
};

const RegisterError = ({ t, redirectCallback, error, setError }: RegisterErrorContentProps) => {
  useEffect(() => {
    error && trackStepRegister("err_info_perso", undefined, error);
    return () => {
      setError(null);
    };
  }, [error]);

  return (
    <div className="register__error">
      <div data-testid="register__error--title" className="super-title-3">
        {t("register.error.unknown.title")}
      </div>
      <div data-testid="register__error--body" className="register__error-body body-text-4">
        <div className="register__error-body-text">{t("register.error.unknown.text")}</div>
        <div className="register__error-body-tips">{t("register.error.unknown.tips")}</div>
      </div>
      <Button
        testID="register__error--button"
        classNames={["register__error-button"]}
        label={t("register.error.unknown.button")}
        onClick={() => {
          trackClickRegister("err_info_perso_retry");
          redirectCallback();
        }}
      />
    </div>
  );
};

export const RegisterContent = ({ state, redirectCallback }: RegisterContentProps) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const {
    t,
    datafeedMemberForm,
    vcesMemberForm,
    jsonForm,
    noEmailJsonForm,
    vcesEnabled,
    onSubmitForm,
    onSubmitFormEmail,
    submitDatafeedMemberForm,
    submitVCESMemberForm,
    onRetryToSendEmail,
    dynamicFormName,
    dynamicFormNoEmailName,
    registerError,
    setRegisterError,
    obfuscatedEmail,
  } = useRegisterContent(state, i18n.language);

  const registerContentMap: Record<string, ReactElement> = {
    "register": (
      <RegisterEntryContent
        name={dynamicFormName}
        initialFormJSON={jsonForm}
        onSubmitForm={onSubmitForm}
      />
    ),
    "register-no-email": (
      <RegisterNoEmailContent
        name={dynamicFormNoEmailName}
        initialFormJSON={noEmailJsonForm}
        onSubmitForm={onSubmitFormEmail}
      />
    ),
    "vces": <VCESContent />,
    "vces-validator": <VCESvalidator />,
    "vces-member-datafeed": (
      <VCESDatafeedContent
        name="vces-member-datafeed"
        initialFormJSON={datafeedMemberForm}
        onSubmitForm={submitDatafeedMemberForm}
      />
    ),
    "vces-member-vces": (
      <VCESMemberContent
        name="vces-member-vces"
        initialFormJSON={vcesMemberForm}
        onSubmitForm={submitVCESMemberForm}
      />
    ),
    "register-member-to-verify": (
      <RegisterMemberToVerifyContent
        t={t}
        onRetryToSendEmail={onRetryToSendEmail}
        obfuscatedEmail={obfuscatedEmail}
      />
    ),
    "register-member-to-verify-confirmation": <RegisterMemberToVerifyConfirmationContent t={t} />,
    "register-member-not-found": (
      <RegisterMemberNotFound
        t={t}
        redirectCallback={redirectCallback}
        error={registerError}
        setError={setRegisterError}
      />
    ),
    "register-member-already-verified": (
      <RegisterMemberAlreadyVerified
        t={t}
        vcesEnabled={vcesEnabled}
        navigate={navigate}
        error={registerError}
        setError={setRegisterError}
      />
    ),
    "register-error": (
      <RegisterError
        t={t}
        redirectCallback={redirectCallback}
        error={registerError}
        setError={setRegisterError}
      />
    ),
  };

  return registerContentMap[state];
};
